import { openConsentManager } from '@segment/consent-manager';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Form, Formik } from 'formik';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button, ButtonTypes } from '@/components/Button';
import { ButtonsTabs } from '@/components/ButtonsTabs';
import { InputCheckbox } from '@/components/form/InputCheckbox';
import { InputField } from '@/components/form/InputField';
import { useAuth } from '@/modules/auth/hooks/useAuth';
import { useLogout } from '@/modules/auth/hooks/useLogout';
import { AccountDeletionModal } from '@/modules/auth/presentation/organisms/modals/AccountDeletionModal';
import { useNotifications } from '@/modules/notifications/hooks/useNotifications';
import { deleteUser, updateUser } from '@/modules/user/domain/resources';
import { User } from '@/types';
import { config } from '@/utils';

export const accountButtons = [
  { link: 'profile' },
  { link: 'subscription', prefetch: true },
  { link: 'billing' },
];

export const ProfilePage = () => {
  const [openDeletionModal, setOpenDeletionModal] = useState(false);
  const { formatMessage } = useIntl();
  const { showNotificationError, showNotificationInfo } = useNotifications();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { logout } = useLogout();
  const handleUpdateUser = useMutation((data: User) => updateUser(user?.UUID, data), {});
  const handleDeleteUser = useMutation((UUID: string) => deleteUser(UUID));

  const shouldNotDeleteAccount = user?.signUpType === 'corporate';

  const onSubmitHandler = async (values) => {
    if (handleUpdateUser.isLoading) return;
    try {
      await handleUpdateUser.mutateAsync(values);
      showNotificationInfo('account.profile.notifications.update.success');

      queryClient.invalidateQueries(['user']);
    } catch (error) {
      showNotificationError('account.profile.notifications.update.error');
    }
  };
  const onDeleteHandler = async (confirmed = false) => {
    if (handleDeleteUser.isLoading) return;
    setOpenDeletionModal(!openDeletionModal);
    if (confirmed) {
      try {
        await handleDeleteUser.mutateAsync(user?.UUID);
        showNotificationInfo('account.profile.notifications.deletion.success');
        logout();
      } catch (error) {
        showNotificationError('account.profile.notifications.deletion.error');
      }
    }
  };

  return !user?.UUID ? null : (
    <div className="content">
      {openDeletionModal && (
        <AccountDeletionModal
          setOpenDeletionModal={setOpenDeletionModal}
          onDeleteHandler={onDeleteHandler}
        />
      )}
      <ButtonsTabs pageAssociated="account" buttons={accountButtons} activeButton="profile" />
      <div className="card m-6">
        <Formik
          initialValues={{
            ...user,
            UUID: user?.UUID ?? '',
            firstName: user?.firstName ?? '',
            lastName: user?.lastName ?? '',
            email: user?.email ?? '',
            newsletter: user?.newsletter ?? false,
          }}
          onSubmit={onSubmitHandler}
        >
          {({ handleSubmit }) => (
            <>
              <h2 className="card-title">{formatMessage({ id: 'account.profile.title' })}</h2>
              <Form className="max-w-2xl">
                <div className="w-full flex flex-col md:flex-row gap-4">
                  <InputField
                    label={formatMessage({ id: 'account.form.label.lastname' })}
                    name="lastName"
                    placeholder={formatMessage({ id: 'account.form.label.lastname' })}
                  />
                  <InputField
                    label={formatMessage({ id: 'account.form.label.firstname' })}
                    name="firstName"
                    placeholder={formatMessage({ id: 'account.form.label.firstname' })}
                  />
                </div>
                <div className="flex w-full">
                  <InputField
                    label={formatMessage({ id: 'account.form.label.email' })}
                    name="email"
                    readOnly={true}
                    placeholder={formatMessage({ id: 'account.form.label.email' })}
                  />
                </div>
                <div className="flex w-full">
                  <InputCheckbox
                    label={formatMessage({ id: 'account.profile.newsletter' })}
                    name="newsletter"
                    description={formatMessage({
                      id: 'account.profile.newsletter.information',
                    })}
                  />
                </div>
                <Button
                  style="primary"
                  type={ButtonTypes.Submit}
                  onClick={handleSubmit}
                  ariaLabel={formatMessage({ id: 'account.form.accessibility.submit' })}
                  disabled={handleUpdateUser.isLoading}
                  className="mt-4 mb-10"
                >
                  {formatMessage({ id: 'account.form.submit' })}
                </Button>
                <p className="block text-brand-neutral-300 font-normal text-sm">
                  {formatMessage({ id: 'account.legals.links' })}
                </p>
                <p
                  className="block font-normal text-sm mt-4 underline"
                  role="button"
                  onClick={openConsentManager}
                >
                  {formatMessage({ id: 'account.profile.consentManager.update' })}
                </p>
              </Form>
            </>
          )}
        </Formik>
      </div>

      <div className="text-xs text-center text-brand-neutral-600 my-5">{`Version: ${config.version}`}</div>

      <p className="mx-10 mb-6 text-sm text-brand-neutral-300 text-center">
        {formatMessage(
          { id: 'account.suggestion.links' },
          {
            suggestImprovement: (children) => (
              <a
                className="text-white"
                href={formatMessage(
                  { id: 'contact.email.content' },
                  { appVersion: config.version, userId: user?.UUID }
                )}
              >
                {children}
              </a>
            ),
          }
        )}
      </p>
      {!shouldNotDeleteAccount && (
        <div className="flex mx-10 md:mb-8 flex-col md:flex-row items-center md:items-start">
          <Button
            style="danger"
            onClick={() => onDeleteHandler()}
            ariaLabel={formatMessage({ id: 'account.delete.accessibility.start' })}
            ariaControls="modal"
          >
            {formatMessage({ id: 'account.form.delete' })}
          </Button>

          <p className="block text-brand-neutral-300 font-normal w-96 text-sm ml-7">
            {formatMessage({ id: 'account.delete.warning' })}
          </p>
        </div>
      )}
    </div>
  );
};
export default ProfilePage;
